/* eslint-disable import/order, simple-import-sort/imports */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { BuyersPicker } from '../formComponents/BuyersPicker';
import { CrossDockPicker } from '../formComponents/CrossDockPicker';
import { ShippersPicker } from '../formComponents/ShippersPicker';
import { VendorPicker } from '../formComponents/VendorPicker';
import { ReasonCodePicker } from '../formComponents/ReasonCodePicker';
import { formatPurchaseOrderDateTime } from '../utils/purchaseOrderDateUtils';
import {
    DELIVERY_TYPE_DELIVERED,
    DELIVERY_TYPE_PICKUP,
    FALLBACK_TEXT,
    formatCurrency,
    normalizeOptionalText,
    normalizeToUpperCase,
} from '../utils/purchaseOrderDisplayUtils';
import { buildPurchaseOrderPrimaryDraft } from '../utils/purchaseOrderCreateAndEditUtils';

import { Alert, AlertDescription, AlertTitle } from '~/shadcn/components/ui/Alert';
import { Button } from '~/shadcn/components/ui/Button';
import { Checkbox } from '~/shadcn/components/ui/Checkbox';
import { Input } from '~/shadcn/components/ui/Input';
import { Label } from '~/shadcn/components/ui/Label';
import { Separator } from '~/shadcn/components/ui/Separator';
import { Textarea } from '~/shadcn/components/ui/Textarea';
import { FacilityPickerV2 } from '~/shared/components/FacilityPickerV2';

/**
 * Editable primary information card for a purchase order. Displays a structured form
 * that mirrors the read-only primary card and lets users update key PO fields.
 *
 * @param {Object} props Component props.
 * @param {Object|null} props.purchaseOrder Purchase order currently being edited.
 * @param {Function} [props.onCancel] Invoked when the user clicks Cancel.
 * @param {Function} [props.onSubmit] Invoked with the updated form state when Save is clicked.
 * @param {boolean} [props.isSubmitting=false] Disables interactions while a save is in progress.
 * @param {Object} [props.draft] Optional controlled draft state (used by the new header-driven save flow).
 * @param {Function} [props.onDraftChange] Optional controlled draft setter.
 * @param {string[]} [props.primaryErrors]
 * @param {Object} [props.primaryFieldErrors]
 * @param {boolean} [props.showValidation]
 * @returns {JSX.Element} Rendered edit card.
 */
export function PurchaseOrderEditPrimaryCard({
    draft,
    isSubmitting = false,
    onCancel,
    onDraftChange,
    onSubmit,
    primaryErrors = [],
    primaryFieldErrors = {},
    purchaseOrder = null,
    showValidation = false,
}) {
    const initialFormState = useMemo(() => buildPurchaseOrderPrimaryDraft(purchaseOrder), [purchaseOrder]);
    const isControlled = draft != null && typeof onDraftChange === 'function';

    const [legacyFormState, setLegacyFormState] = useState(initialFormState);

    useEffect(() => {
        if (isControlled) return;
        setLegacyFormState(initialFormState);
    }, [initialFormState, isControlled]);

    const formState = isControlled ? draft : legacyFormState;

    const setFormState = useCallback(
        (updater) => {
            if (isControlled) {
                onDraftChange((previous) => {
                    const base = previous ?? initialFormState;
                    return typeof updater === 'function' ? updater(base) : updater;
                });
                return;
            }

            setLegacyFormState((previous) => {
                const base = previous ?? initialFormState;
                return typeof updater === 'function' ? updater(base) : updater;
            });
        },
        [initialFormState, isControlled, onDraftChange],
    );

    const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';
    const readOnlyValueClassName = 'text-left text-base font-medium text-muted-foreground/70';

    const readOnlyValues = useMemo(
        () => ({
            poNumber: normalizeOptionalText(purchaseOrder?.id),
            status: normalizeOptionalText(purchaseOrder?.status),
            createdOn: formatPurchaseOrderDateTime(purchaseOrder?.createdAt, FALLBACK_TEXT),
            produceOrGrocery: normalizeOptionalText(purchaseOrder?.produceOrGrocery),
            truckID: normalizeOptionalText(purchaseOrder?.truckID),
            totalCost: formatCurrency(purchaseOrder?.poTotalCost),
        }),
        [purchaseOrder],
    );

    /**
     * Determines if a reason code is required based on whether certain fields have been modified.
     * Reason code is required when pickupDate, shipperId, or deliveryType are changed.
     */
    const requiresReasonCode = useMemo(() => {
        const pickupDateChanged = formState.pickupDate !== initialFormState.pickupDate;
        const shipperIdChanged = formState.shipperId !== initialFormState.shipperId;
        const deliveryTypeChanged = formState.deliveryType !== initialFormState.deliveryType;
        return pickupDateChanged || shipperIdChanged || deliveryTypeChanged;
    }, [formState.pickupDate, formState.shipperId, formState.deliveryType, initialFormState]);

    const handleFieldChange = useCallback(
        (field, transformValue) => (event) => {
            const rawValue = event?.target?.value ?? '';
            const value = typeof transformValue === 'function' ? transformValue(rawValue) : rawValue;
            setFormState((previous) => ({
                ...previous,
                [field]: value,
            }));
        },
        [setFormState],
    );

    const handleFacilityChange = useCallback(
        (facility) => {
            setFormState((previous) => ({
                ...previous,
                facilityId: facility?.id ?? '',
            }));
        },
        [setFormState],
    );

    const handleVendorChange = useCallback(
        (vendorId) => {
            setFormState((previous) => ({
                ...previous,
                vendorId: vendorId ?? null,
            }));
        },
        [setFormState],
    );

    const handleBuyersChange = useCallback(
        (buyerIds) => {
            setFormState((previous) => ({
                ...previous,
                buyers: Array.isArray(buyerIds) ? buyerIds : [],
            }));
        },
        [setFormState],
    );

    const handleShipperChange = useCallback(
        (shipperId) => {
            setFormState((previous) => ({
                ...previous,
                shipperId: shipperId ?? null,
            }));
        },
        [setFormState],
    );

    const handleCrossDockChange = useCallback(
        (crossDockId) => {
            setFormState((previous) => ({
                ...previous,
                crossDockId: crossDockId ?? null,
            }));
        },
        [setFormState],
    );

    const handlePriorityChange = useCallback(
        (checked) => {
            setFormState((previous) => ({
                ...previous,
                priority: checked === true,
            }));
        },
        [setFormState],
    );

    const handleReasonCodeChange = useCallback(
        (value) => {
            setFormState((previous) => ({
                ...previous,
                reasonCode: value,
            }));
        },
        [setFormState],
    );

    const handleSubmit = useCallback(
        (event) => {
            event?.preventDefault();

            if (typeof onSubmit !== 'function') return;

            if (requiresReasonCode && !formState.reasonCode) {
                const reasonCodeTrigger = document.getElementById('primary-reason-code-trigger');
                if (reasonCodeTrigger) reasonCodeTrigger.focus();
                return;
            }

            onSubmit(formState);
        },
        [formState, onSubmit, requiresReasonCode],
    );

    const showErrors = showValidation === true;
    const getFieldError = useCallback(
        (field) => {
            const shouldShowFieldError = showErrors || (field === 'reasonCode' && requiresReasonCode);
            if (!shouldShowFieldError) return '';
            return primaryFieldErrors?.[field] ?? '';
        },
        [primaryFieldErrors, requiresReasonCode, showErrors],
    );

    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-1">
                    <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">
                        Edit Purchase Order Information
                    </h2>
                    <p className="text-left text-sm text-muted-foreground">
                        {isControlled
                            ? 'Update the values below. Use the header controls to save or cancel your edits.'
                            : 'Update the values below and click Save Changes to keep your edits.'}
                    </p>
                </div>

                {showErrors && primaryErrors.length > 0 ? (
                    <Alert variant="destructive">
                        <AlertTitle className="text-left">Fix the following to save this purchase order</AlertTitle>
                        <AlertDescription className="text-left">
                            <ul className="list-disc pl-5 text-left">
                                {primaryErrors.map((message) => (
                                    <li key={message}>{message}</li>
                                ))}
                            </ul>
                        </AlertDescription>
                    </Alert>
                ) : null}

                {/* Read-only information section */}
                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>PO #</span>
                        <span className={readOnlyValueClassName}>{readOnlyValues.poNumber}</span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Status</span>
                        <span className={readOnlyValueClassName}>{readOnlyValues.status}</span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Created On</span>
                        <span className={readOnlyValueClassName}>{readOnlyValues.createdOn}</span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Produce/Grocery</span>
                        <span className={readOnlyValueClassName}>{readOnlyValues.produceOrGrocery}</span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Truck ID</span>
                        <span className={readOnlyValueClassName}>{readOnlyValues.truckID}</span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className={labelClassName}>Total Cost</span>
                        <span className={readOnlyValueClassName}>{readOnlyValues.totalCost}</span>
                    </div>
                </div>

                {/* Separator between read-only and editable sections */}
                <Separator />

                {/* Editable fields section */}
                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                    <FacilityPickerV2
                        label="Facility"
                        value={formState.facilityId}
                        onValueChange={handleFacilityChange}
                    />

                    <BuyersPicker value={formState.buyers} onValueChange={handleBuyersChange} />

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-3 pt-6">
                            <Checkbox
                                id="priority"
                                checked={formState.priority}
                                onCheckedChange={handlePriorityChange}
                                aria-describedby="priority-helper"
                            />
                            <Label htmlFor="priority" className="text-sm font-medium leading-none">
                                Mark as Priority
                            </Label>
                        </div>
                    </div>

                    <fieldset className="flex flex-col gap-3">
                        <legend className={labelClassName} style={{ paddingBottom: '15px' }}>
                            Delivery Type
                        </legend>
                        <div className="flex flex-wrap gap-3">
                            <Label
                                htmlFor="deliveryType-pickup"
                                className="flex items-center gap-2 text-sm font-medium leading-none"
                            >
                                <input
                                    type="radio"
                                    id="deliveryType-pickup"
                                    name="deliveryType"
                                    value={DELIVERY_TYPE_PICKUP}
                                    checked={formState.deliveryType === DELIVERY_TYPE_PICKUP}
                                    onChange={handleFieldChange('deliveryType', normalizeToUpperCase)}
                                    className="size-4"
                                />
                                PICK UP
                            </Label>
                            <Label
                                htmlFor="deliveryType-delivered"
                                className="flex items-center gap-2 text-sm font-medium leading-none"
                            >
                                <input
                                    type="radio"
                                    id="deliveryType-delivered"
                                    name="deliveryType"
                                    value={DELIVERY_TYPE_DELIVERED}
                                    checked={formState.deliveryType === DELIVERY_TYPE_DELIVERED}
                                    onChange={handleFieldChange('deliveryType', normalizeToUpperCase)}
                                    className="size-4"
                                />
                                VENDOR-DELIVERED
                            </Label>
                        </div>
                    </fieldset>

                    <VendorPicker value={formState.vendorId} onValueChange={handleVendorChange} />

                    <ShippersPicker value={formState.shipperId} onValueChange={handleShipperChange} />

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="pickupDate" className={labelClassName}>
                            Pick Up Date
                        </Label>
                        <Input
                            id="pickupDate"
                            name="pickupDate"
                            type="date"
                            value={formState.pickupDate}
                            onChange={handleFieldChange('pickupDate')}
                            placeholder="YYYY-MM-DD"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="scheduledDeliveryDate" className={labelClassName}>
                            Scheduled Delivery
                        </Label>
                        <Input
                            id="scheduledDeliveryDate"
                            name="scheduledDeliveryDate"
                            type="datetime-local"
                            value={formState.scheduledDeliveryDate}
                            onChange={handleFieldChange('scheduledDeliveryDate')}
                            placeholder="YYYY-MM-DDTHH:mm"
                        />
                    </div>

                    <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-1">
                        <span className={labelClassName}>Temperature (Min - Max)</span>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-2">
                                <Label
                                    htmlFor="minTempReq"
                                    className="text-sm font-medium text-foreground whitespace-nowrap"
                                >
                                    Min
                                </Label>
                                <Input
                                    id="minTempReq"
                                    name="minTempReq"
                                    type="number"
                                    step="1"
                                    value={formState.minTempReq}
                                    onChange={handleFieldChange('minTempReq')}
                                    placeholder="Enter minimum temperature"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Label
                                    htmlFor="maxTempReq"
                                    className="text-sm font-medium text-foreground whitespace-nowrap"
                                >
                                    Max
                                </Label>
                                <Input
                                    id="maxTempReq"
                                    name="maxTempReq"
                                    type="number"
                                    step="1"
                                    value={formState.maxTempReq}
                                    onChange={handleFieldChange('maxTempReq')}
                                    placeholder="Enter maximum temperature"
                                />
                            </div>
                        </div>
                    </div>

                    <CrossDockPicker value={formState.crossDockId} onValueChange={handleCrossDockChange} />

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="crossDockDeliveryDate" className={labelClassName}>
                            Cross Dock Delivery Date
                        </Label>
                        <Input
                            id="crossDockDeliveryDate"
                            name="crossDockDeliveryDate"
                            type="date"
                            value={formState.crossDockDeliveryDate}
                            onChange={handleFieldChange('crossDockDeliveryDate')}
                            placeholder="YYYY-MM-DD"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="estimatedFreightCost" className={labelClassName}>
                            Freight Cost
                        </Label>
                        <Input
                            id="estimatedFreightCost"
                            name="estimatedFreightCost"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            value={formState.estimatedFreightCost}
                            onChange={handleFieldChange('estimatedFreightCost')}
                            placeholder="Enter freight cost"
                        />
                    </div>

                    <div className="flex flex-col gap-2 sm:col-span-2 xl:col-span-2">
                        <Label htmlFor="specialInstructions" className={labelClassName}>
                            Special Instructions
                        </Label>
                        <Textarea
                            id="specialInstructions"
                            name="specialInstructions"
                            value={formState.specialInstructions}
                            onChange={handleFieldChange('specialInstructions')}
                            placeholder="Add any special instructions for this purchase order"
                            rows={4}
                        />
                    </div>

                    {requiresReasonCode && (
                        <div className="flex flex-col gap-2">
                            <ReasonCodePicker
                                label={
                                    <>
                                        Reason <span className="text-destructive">*</span>
                                    </>
                                }
                                value={formState.reasonCode}
                                onValueChange={handleReasonCodeChange}
                                triggerId="primary-reason-code-trigger"
                            />
                            {getFieldError('reasonCode') ? (
                                <p className="text-xs text-destructive">{getFieldError('reasonCode')}</p>
                            ) : null}
                        </div>
                    )}
                </div>
                {!isControlled && (
                    <div className="flex flex-wrap justify-end gap-2">
                        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </div>
                )}
            </form>
        </div>
    );
}

PurchaseOrderEditPrimaryCard.displayName = 'PurchaseOrderEditPrimaryCard';
